import { CURRENCY, PLANS, yearlyPrice } from '@/modules/marketing/pricing-data';
import type { Plan } from '@/types/api';

/**
 * What the public pricing page says a plan costs, so the admin can show when it disagrees.
 *
 * ## Why this check exists
 *
 * It has now gone wrong twice. The pricing page was rebuilt to say Business is $49 a month while
 * the catalogue still held $79, and nothing anywhere reported it — the page rendered, the checkout
 * opened, the payment succeeded, and the figure a customer had agreed to was not the figure they
 * were charged. That is the worst shape a defect can take in a billing path, because every part of
 * the system reports success.
 *
 * The page and the catalogue are edited in different places by different means (a constant in the
 * repository, and a form in the admin), so they will drift again. This makes the drift visible on
 * the row rather than waiting for a customer to find it.
 *
 * ## The third place, which this cannot see
 *
 * `price_minor` is what the platform *displays and records*. It is not what the customer is
 * charged. The checkout sends the gateway a price id and nothing else, so Paddle charges whatever
 * that price is configured for at Paddle.
 *
 * Three numbers therefore have to agree: this page, this catalogue, and the gateway. Two of them
 * are checked here. The third is only ever right because somebody went and looked, which is why
 * the warning says so out loud instead of implying the platform has it covered.
 */

/** Only the plans this platform seeds. An operator's own plans are not ours to have an opinion on. */
const SEEDED = /^(starter|pro|business)-(monthly|yearly)$/;

/**
 * The product the public pricing page is about.
 *
 * Scoping to it is not defensive tidiness. A plan slug is unique *per product* — the schema says
 * so, `plans_product_slug_uniq` is on `(product_id, slug)` — and the first run of this check duly
 * matched a `business-yearly` belonging to an entirely different product and reported that the
 * pricing page advertised $490 for a $990 plan. It advertises nothing for that plan at all.
 */
const PRICED_PRODUCT = 'platform';

/**
 * The price in minor units the public page advertises for this plan, or `null` when the page makes
 * no claim about it — another product, a custom plan, or one priced in another currency.
 */
export function advertisedPriceMinor(plan: Plan, productSlug: string): number | null {
  if (productSlug.toLowerCase() !== PRICED_PRODUCT) {
    return null;
  }

  const match = SEEDED.exec(plan.slug.toLowerCase());

  if (match === null || plan.currency.toUpperCase() !== CURRENCY) {
    return null;
  }

  const [, name, interval] = match;
  const page = PLANS.find((candidate) => candidate.name.toLowerCase() === name);

  if (page === undefined) {
    return null;
  }

  return (interval === 'yearly' ? yearlyPrice(page) : page.monthly) * 100;
}

/** Formats minor units the way the pricing page writes them, for the warning text. */
export function formatMinor(minor: number, currency: string): string {
  return `${currency.toUpperCase()} ${(minor / 100).toFixed(2)}`;
}
